import { showAddOrEditStudyLevelState } from "@/atoms";
import CustomInput from "@/components/common/inputs/CustomInput";
import useAuth from "@/hooks/useAuth";
import { useUserCouncil } from "@/hooks/useUserCouncil";
import { useGetAllCouncils } from "@/services/councils";
import { useGetServicesByCouncil } from "@/services/services";
import { useCreateStudyLevelConfiguration, useUpdateStudyLevelConfiguration } from "@/services/study-level-configuration";
import { Button, Select } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { ModalLayout } from "moh-ui";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";

export default function AddUpdateStudyLevel() {
    const [show, setShow] = useRecoilState(showAddOrEditStudyLevelState);
    const { user } = useAuth();
  const council = useUserCouncil();
    const [data, setData] = useState({
        studyLevelName: "",
        councilId: null as string | null,
        serviceId: null as string | null,
    });

    const {
        mutate: createStudyLevel,
        isPending: creatingStudyLevel
    } = useCreateStudyLevelConfiguration();

    const {
        mutate: updateStudyLevel,
        isPending: updatingStudyLevel
    } = useUpdateStudyLevelConfiguration();

    const queryClient = useQueryClient();

    const {
        data: services,
    } = useGetServicesByCouncil({
        councilId: data.councilId || undefined,
        enabled: !!data.councilId,
    });

    const {
        data: councils,
    } = useGetAllCouncils();

    useEffect(() => {
        if (show?.action === 'edit' && show.studyLevel) {
            setData({
                studyLevelName: show.studyLevel.studyLevelName,
                councilId: show.studyLevel.council.id,
                serviceId: show.studyLevel.service.id,
            });
        } else {
            setData({
                studyLevelName: "",
                councilId: user?.roles?.[0]?.roleName === "ADMINCOUNCIL" ? (council?.id ?? null) : null,
                serviceId: null,
            });
        }
    }, [show, user]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (show?.action === 'add') {
            createStudyLevel({
                ...data,
                councilId: data.councilId!,
                serviceId: data.serviceId!,
            }, {
                onSuccess: () => {
                    notifications.show({
                        title: "Success",
                        message: "Study Level Added Successfully",
                        color: "green",
                    });
                    if (user?.roles?.[0]?.roleName === "ADMINCOUNCIL") {
                        queryClient.invalidateQueries({
                            queryKey: ["study-level-configurations", council?.id],
                        })
                    } else {
                        queryClient.invalidateQueries({
                            queryKey: ["study-level-configurations"],
                        })
                    }
                    setShow(null);
                },
                onError: (error) => {
                    notifications.show({
                        title: "Error",
                        message: error.message || "Failed to add study level",
                        color: "red",
                    });
                }
            });
        } else if (show?.action === 'edit' && show.studyLevel) {
            updateStudyLevel({
                id: show.studyLevel.id!,
                ...data,
                councilId: data.councilId!,
                serviceId: data.serviceId!,
            }, {
                onSuccess: () => {
                    notifications.show({
                        title: "Success",
                        message: "Study Level Updated Successfully",
                        color: "green",
                    });
                    if (user?.roles?.[0]?.roleName === "ADMINCOUNCIL") {
                        queryClient.invalidateQueries({
                            queryKey: ["study-level-configurations", council?.id],
                        })
                    } else {
                        queryClient.invalidateQueries({
                            queryKey: ["study-level-configurations"],
                        })
                    }
                    setShow(null);
                },
                onError: (error) => {
                    notifications.show({
                        title: "Error",
                        message: error.message || "Failed to update study level",
                        color: "red",
                    });
                }
            });
        }
    };

    return (
        <ModalLayout
            open={Boolean(show?.show)}
            onClose={() => setShow(null)}
        >
            <div className="mb-5">
                <h2 className="text-lg font-semibold">
                    {show?.action === 'add' ? 'Add Study Level' : 'Update Study Level'}
                </h2>
                <p className="text-sm text-gray-500">
                    {show?.action === 'add' ? 'Fill in the details to create a new study level.' : 'Update the study level details as needed.'}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <CustomInput
                    type="text"
                    value={data.studyLevelName}
                    onChange={(e) => setData({ ...data, studyLevelName: e.target.value })}
                    label="Study Level Name"
                    placeholder="Enter Study Level Name"
                    required
                    alwaysFocused
                />

                {user?.roles?.[0]?.roleName === "ADMIN" && (
                    <CustomInput label="Select Council" alwaysFocused>
                        <Select
                            value={data.councilId ?? ""}
                            onChange={(val) => setData({ ...data, councilId: val })}
                            data={councils?.data.map((item) => ({
                                value: item.id,
                                label: item.councilName + " (" + item.councilCode + ")",
                            })) || []}
                            placeholder="Select Council"
                            required
                            className="w-full"
                        />
                    </CustomInput>
                )}

                <CustomInput label="Select Service" alwaysFocused>
                    <Select
                        value={data.serviceId ?? ""}
                        onChange={(val) => setData({ ...data, serviceId: val })}
                        data={services?.data?.map((item) => ({
                            value: item.id,
                            label: item.name,
                        })) || []}
                        placeholder="Select Service"
                        disabled={!data.councilId}
                        required
                        className="w-full"
                    />
                </CustomInput>

                <div className="flex justify-end items-center gap-4 pt-4 border-t">
                    <Button
                        variant="outline"
                        color="gray"
                        onClick={() => setShow(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        loading={creatingStudyLevel || updatingStudyLevel}
                    >
                        {show?.action === 'add' ? 'Create Study Level' : 'Update Study Level'}
                    </Button>
                </div>
            </form>
        </ModalLayout>
    );
}
